import capitalize from 'lodash/capitalize'

import { PagePaths, pages } from '~/app/routes'
import { DATE_TEMPLATES, day, diff, format, getOnlyDate, getToday, isSame, isSameDate } from '~/astra'
import env from '~/env'
import { getFlag } from '~/hooks/useFlag'
import { DepartmentKey } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { type Include, selectLocation, selectLocationServiceOverride, type Surgery, type SurgeryTypeGroup as StormSurgeryTypeGroup } from '~/storm'
import { getDayOvernightLabel, isDayOvernightLabel } from '~/utils/dayOvernight'
import {
    getAssistants,
    getLevelOfCareNprName,
    getMainSurgeon,
    getMainSurgeonAndAssistants,
    getScheduledTime,
    getTheaterRoom,
    isScheduledSurgery,
    isSurgeryLetterSent,
    isUnScheduledSurgery,
    sortByMainSurgeonFirst,
} from '~/utils/dips'
import { isNullish } from '~/utils/guards'
import { getOpeningHours } from '~/utils/openingHours'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

import { AvailabilityPeriod, getIsWaiting, getPatientAvailabilityFromSurgery } from '../Drawer/PatientDrawerTab/StatusesAndRemarks/PatientAvailability/utils'
import { ClearedStatus } from '../Drawer/PatientDrawerTab/StatusesAndRemarks/Statuses/ClearedRadioGroup'
import { FormattedFollowUpTask } from './FollowUpTask'
import { FormattedRemark } from './Remarks'
import { SurgeryMetadataArgs } from './saveSurgeryMetadata'
import { FormattedStatus } from './Statuses'

export const nullishDiagnosisGroupName = 'Uspesifisert'

/**
 * Checks if a surgery has an active infection condition.
 * Only applicable to unscheduled surgeries.
 */
export function hasActiveInfectionCondition(surgery: Surgery): boolean {
    if (!isUnScheduledSurgery(surgery)) return false

    return (
        surgery.criticalInformation?.hasInfectionCondition === true ||
        surgery.criticalInformation?.details?.some(detail => detail.criticalInformationType === 'ConditionInfection' && detail.isActive === true) === true
    )
}

/**
 * Gets the active infection type names for a surgery.
 * Only applicable to unscheduled surgeries.
 */
export function getActiveInfectionTypes(surgery: Surgery): string[] {
    if (!isUnScheduledSurgery(surgery)) return []

    return (surgery.criticalInformation?.details
        ?.filter(detail => detail.criticalInformationType === 'ConditionInfection' && detail.isActive === true)
        .map(detail => detail.infectionType)
        .filter(Boolean) ?? []) as string[]
}

export function shouldShowASAColumn(departmentId: DepartmentKey) {
    const envVar = String(env.VITE_WAITINGLIST_HIDE_ASA_FOR_DEPARTMENT_IDS).split(',')
    return !envVar.includes(String(departmentId))
}

export function isAllOrScheduledView(activeView: PagePaths['WAITING_LIST']) {
    return activeView === pages.WAITING_LIST.views.all.path || activeView === pages.WAITING_LIST.views.scheduled.path
}

export function isAllOrUnscheduledView(activeView: PagePaths['WAITING_LIST']) {
    return activeView === pages.WAITING_LIST.views.all.path || activeView === pages.WAITING_LIST.views.unscheduled.path
}

export function isMigrationView(activeView: PagePaths['WAITING_LIST']) {
    return activeView === pages.WAITING_LIST.views.migration.path
}

export function shouldShowFollowUpColumn(activeView: PagePaths['WAITING_LIST']) {
    const hasMetadataFollowUpFlags =
        getFlag('surgery_metadata__follow_up_self_declaration_form') ||
        getFlag('surgery_metadata__follow_up_external_document') ||
        getFlag('surgery_metadata__follow_up_test_results')

    if (isAllOrScheduledView(activeView)) {
        return hasMetadataFollowUpFlags || getFlag('dips__booking_letter_status')
    }

    return hasMetadataFollowUpFlags
}

/**
 * Storm-typed surgery type group with the relations `getSurgeryCategoryName`
 * reads: `surgeryTypes` (for `external_surgery_type_id`), `subgroups`
 * (for nested-category resolution), and `specialities` (for narrowing when
 * multiple categories match). Denormalizer `SurgeryTypeGroup[]` and the
 * `getSurgeryTypeGroup.all` getter from `~/store/selectors` satisfy it
 * structurally (§10 of the shallow-selectors documentation).
 */
export type SurgeryTypeGroupForCategoryName = Include<StormSurgeryTypeGroup, { surgeryTypes: true; specialities: true; subgroups: true }>

function categoryResolvesSurgeryType(
    category: SurgeryTypeGroupForCategoryName,
    surgeryTypeId: number,
    groupsById: Map<number, SurgeryTypeGroupForCategoryName>
): boolean {
    const visited = new Set<number>()
    const stack: SurgeryTypeGroupForCategoryName[] = [category]
    while (stack.length > 0) {
        const current = stack.pop()
        if (!current || visited.has(current.id)) continue
        visited.add(current.id)
        for (const association of current.surgeryTypes) {
            if (association.external_surgery_type_id === surgeryTypeId) return true
        }
        for (const subgroupRef of current.subgroups) {
            const subgroup = groupsById.get(subgroupRef.id)
            if (subgroup) stack.push(subgroup)
        }
    }
    return false
}

/**
 * Returns the display name of the (top-level) surgery category that contains
 * `surgery.surgeryType`. Walks each category's nested subgroups by id through
 * `surgeryTypeGroups` so a surgery whose type is attached to a deeply nested
 * child still resolves to the top-level category — mirroring
 * `resolveSurgeryTypeGroups` in
 * `storm/common/resolveSurgeryTypeGroups.ts`.
 *
 * When multiple categories match the same `external_surgery_type_id` (e.g.
 * generic types like "Andre operasjoner/inngrep" attached to several
 * categories), the surgery's `section.speciality_id` is used to pick the
 * category whose `specialities` covers that section.
 */
type SurgeryWithSection = Include<Surgery, { section: true }>

function hasSection(surgery: Surgery): surgery is SurgeryWithSection {
    return 'section' in surgery
}

function getSectionSpecialityId(surgery: Surgery): number | undefined {
    return hasSection(surgery) ? (surgery.section?.speciality_id ?? undefined) : undefined
}

export function getSurgeryCategoryName(surgery: Surgery, surgeryTypeGroups: SurgeryTypeGroupForCategoryName[]): string | undefined {
    const surgeryTypeId = surgery.surgeryType?.id
    if (!surgeryTypeId) return undefined

    const groupsById = new Map(surgeryTypeGroups.map(group => [group.id, group]))

    const matchingCategories: SurgeryTypeGroupForCategoryName[] = []
    for (const group of surgeryTypeGroups) {
        if (group.group_type !== 'category') continue
        if (categoryResolvesSurgeryType(group, surgeryTypeId, groupsById)) {
            matchingCategories.push(group)
        }
    }

    if (matchingCategories.length > 1) {
        const specialityId = getSectionSpecialityId(surgery)
        const bySpeciality = matchingCategories.filter(category => category.specialities.some(speciality => speciality.id === specialityId))
        if (bySpeciality.length > 1) {
            console.warn('Surgery type has multiple surgery categories', {
                surgeryTypeId,
                specialityId,
                categoryIds: bySpeciality.map(c => c.id),
            })
        }
        return bySpeciality.at(0)?.display_name
    }

    return matchingCategories.at(0)?.display_name
}

export function getClearedStatus(surgery: Surgery): ClearedStatus {
    const patientReady = getSurgeryMetadata(surgery)?.patient_ready

    if (isUnScheduledSurgery(surgery) && isNullish(patientReady)) return 'new'
    else if (patientReady) return 'cleared'
    return 'notCleared'
}
export function getIsConfirmed(surgery: Surgery) {
    return Boolean(getSurgeryMetadata(surgery)?.patient_confirmed)
}
export function getIsShortNotice(surgery: Surgery) {
    return Boolean(getSurgeryMetadata(surgery)?.patient_short_notice)
}
export function getIsPriority(surgery: Surgery) {
    return Boolean(getSurgeryMetadata(surgery)?.patient_prioritized)
}
export function getIsCancerSurgery(surgery: Surgery) {
    return Boolean(getSurgeryMetadata(surgery)?.surgery_cancer)
}
export function selfDeclarationFormNeedsFollowUp(_surgery: Surgery) {
    return getFlag('surgery_metadata__follow_up_self_declaration_form') && Boolean(getSurgeryMetadata(_surgery)?.self_declaration_form)
}
export function externalDocumentNeedsFollowUp(_surgery: Surgery) {
    return getFlag('surgery_metadata__follow_up_external_document') && Boolean(getSurgeryMetadata(_surgery)?.external_document)
}
export function testResultsNeedsFollowUp(_surgery: Surgery) {
    return getFlag('surgery_metadata__follow_up_test_results') && Boolean(getSurgeryMetadata(_surgery)?.test_results)
}
export function letterNotSent(surgery: Surgery) {
    return getFlag('dips__booking_letter_status') && isScheduledSurgery(surgery) && !isSurgeryLetterSent(surgery)
}
export function getIsNew(surgery: Surgery) {
    const surgeryMetadata = getSurgeryMetadata(surgery)
    const isPatientNew = surgeryMetadata?.patient_ready === undefined || surgeryMetadata.patient_ready === null
    return Boolean(isPatientNew)
}

function isScheduledOnClosedLocationDay(surgery: Surgery): boolean {
    if (!isScheduledSurgery(surgery)) return false

    const roomCode = getTheaterRoom(surgery.surgeryResources)?.shortName
    if (!roomCode) return false

    const state = useStore.getState()

    const location = selectLocation(state, { where: { room_code: roomCode } })
    if (!location) return false

    const date = getScheduledTime(surgery)
    const locationServiceOverride = selectLocationServiceOverride(state, {
        where: { location_uuid: location.uuid, scheduled_date: getOnlyDate(date) },
    })

    return getOpeningHours({ date, location, locationServiceOverride }).isClosed
}

type FormatRemarksArgs = {
    surgery: Surgery
}

export function formatRemarks({ surgery }: FormatRemarksArgs): FormattedRemark[] {
    const formattedRemarks: FormattedRemark[] = []

    const levelOfCareNprName = getLevelOfCareNprName(surgery)
    const dayOvernightLabel = getDayOvernightLabel(levelOfCareNprName) ?? ''
    if (!isDayOvernightLabel(dayOvernightLabel)) formattedRemarks.push({ remark: 'ugyldig dag/heldøgn' })

    if (hasActiveInfectionCondition(surgery)) {
        const infectionTypes = getActiveInfectionTypes(surgery)
        const tooltip = infectionTypes.length > 0 ? `Smitte: ${infectionTypes.join(', ')}` : undefined
        formattedRemarks.push({ remark: 'smitte', tooltip })
    }

    if (getIsShortNotice(surgery)) formattedRemarks.push({ remark: 'kort varsel' })

    if (getIsPriority(surgery)) formattedRemarks.push({ remark: 'prioritert' })

    const showSurgeryCancerSuggestions = getFlag('cancer_features__show')
    if (showSurgeryCancerSuggestions && getIsCancerSurgery(surgery)) formattedRemarks.push({ remark: 'kreftoperasjon' })

    if (isScheduledOnClosedLocationDay(surgery)) formattedRemarks.push({ remark: 'stengt stue' })

    return formattedRemarks
}

export function formatFollowUpTasks(surgery: Surgery): FormattedFollowUpTask[] {
    const tasks: FormattedFollowUpTask[] = []

    if (letterNotSent(surgery)) tasks.push('brev ikke sendt')
    if (externalDocumentNeedsFollowUp(surgery)) tasks.push('eksternt dokument')
    if (selfDeclarationFormNeedsFollowUp(surgery)) tasks.push('egenerklæringsskjema')
    if (testResultsNeedsFollowUp(surgery)) tasks.push('prøvesvar')

    return tasks
}

export function formatFollowUpTasksFromMetadataArgs(mergedMetadata: SurgeryMetadataArgs): FormattedFollowUpTask[] {
    const tasks: FormattedFollowUpTask[] = []

    if (mergedMetadata.externalDocumentNeedsFollowUp) tasks.push('eksternt dokument')
    if (mergedMetadata.selfDeclarationFormNeedsFollowUp) tasks.push('egenerklæringsskjema')
    if (mergedMetadata.testResultsNeedsFollowUp) tasks.push('prøvesvar')

    return tasks
}

export function formatRemarksFromMetadataArgs(mergedMetadata: SurgeryMetadataArgs) {
    const formattedRemarks: FormattedRemark[] = []

    if (mergedMetadata.isShortNotice) formattedRemarks.push({ remark: 'kort varsel' })
    if (mergedMetadata.isPriority) formattedRemarks.push({ remark: 'prioritert' })
    const showSurgeryCancerSuggestions = getFlag('cancer_features__show')
    if (showSurgeryCancerSuggestions && mergedMetadata.isCancerSurgery) formattedRemarks.push({ remark: 'kreftoperasjon' })

    return formattedRemarks
}

export function formatStatuses(surgery: Surgery) {
    const formattedStatuses: FormattedStatus[] = []
    const clearedStatus = getClearedStatus(surgery)

    if (clearedStatus === 'new') formattedStatuses.push('ny')
    else if (clearedStatus === 'cleared') formattedStatuses.push('klarert')

    if (getSurgeryMetadata(surgery)?.patient_confirmed) formattedStatuses.push('bekreftet')

    if (getIsWaiting(surgery)) formattedStatuses.push('avvent')

    return formattedStatuses
}

export function formatStatusesFromMetadataArgs(mergedMetadata: SurgeryMetadataArgs) {
    const formattedStatuses: FormattedStatus[] = []

    // No readiness set from any source (saved, suggested, or user-edited) only happens for an
    // untriaged patient, so show "Ny" instead of letting the cleared badge default to "Ikke
    // klarert". An explicit value takes precedence.
    if (mergedMetadata.isCleared) formattedStatuses.push('klarert')
    else if (mergedMetadata.isCleared === undefined) formattedStatuses.push('ny')

    if (mergedMetadata.isConfirmed) formattedStatuses.push('bekreftet')

    if (mergedMetadata.isWaiting) formattedStatuses.push('avvent')

    return formattedStatuses
}
export function formatAvailabilityPeriodLabel(period: AvailabilityPeriod): string {
    if (period.type === 'availableFrom') {
        const today = getToday()
        const startDate = day(period.startDate)

        if (isSameDate(startDate, today)) {
            return 'Fra i dag'
        }
        return `Fra ${format(startDate, DATE_TEMPLATES.dateShortMonth)}`
    }

    period.type satisfies 'unavailableBetween'

    if (isSame(period.startDate, period.endDate, 'day')) {
        return `${format(day(period.endDate), DATE_TEMPLATES.dateShortMonth)}`
    } else if (isSame(period.startDate, period.endDate, 'month')) {
        return `${format(period.startDate, 'D.')}–${format(day(period.endDate), DATE_TEMPLATES.dateShortMonth)}`
    }
    return `${format(period.startDate, DATE_TEMPLATES.dateShortMonth)}–${format(day(period.endDate), DATE_TEMPLATES.dateShortMonth)}`
}

export function formatPatientAvailability(surgery: Surgery) {
    return getPatientAvailabilityFromSurgery(surgery).map(formatAvailabilityPeriodLabel)
}

export function formatPatientAvailabilityFromMetadataArgs(mergedMetadata: SurgeryMetadataArgs) {
    return mergedMetadata.patientAvailability?.map(formatAvailabilityPeriodLabel) ?? []
}

export function formatSurgeonsAndAssistants(surgery: Surgery) {
    const practitioners = getMainSurgeonAndAssistants(surgery.surgeryResources)
    const sortedPractitioners = practitioners.toSorted(sortByMainSurgeonFirst)
    const shortNames = sortedPractitioners.map(({ shortName }) => shortName?.toLocaleUpperCase())

    return shortNames.filter(Boolean)
}

export function formatSurgeonsAndAssistantsCombo(surgery: Surgery, emptyShortName = '[–]') {
    const mainSurgeon = getMainSurgeon(surgery.surgeryResources)
    const assistants = getAssistants(surgery.surgeryResources)
    const practitionerShortNames = [mainSurgeon, ...assistants].map(practitioner => practitioner?.shortName?.toLocaleUpperCase() || emptyShortName)

    if (practitionerShortNames.every(practitioner => practitioner === emptyShortName)) return 'Ikke tildelt kirurg'
    return practitionerShortNames.join(' + ')
}

export function buildPractitionerGroupCounters(surgeries: readonly Surgery[]) {
    const groupCounters: Record<string, Record<string, number>> = {}
    let unassignedCount = 0

    for (const surgery of surgeries) {
        const mainSurgeon = getMainSurgeon(surgery.surgeryResources)?.shortName
        const assistants = getAssistants(surgery.surgeryResources)
            .map(a => a.shortName)
            .filter(Boolean)

        if (mainSurgeon && assistants.length === 0) {
            // Main surgeon alone
            if (!groupCounters[mainSurgeon]) {
                groupCounters[mainSurgeon] = {}
            }
            groupCounters[mainSurgeon]![mainSurgeon] = (groupCounters[mainSurgeon]![mainSurgeon] ?? 0) + 1
        } else if (mainSurgeon && assistants.length > 0) {
            // Main surgeon + assistant(s)
            if (!groupCounters[mainSurgeon]) {
                groupCounters[mainSurgeon] = {}
            }
            assistants.forEach(assistant => {
                const key = `${mainSurgeon} + ${assistant}`
                groupCounters[mainSurgeon]![key] = (groupCounters[mainSurgeon]![key] ?? 0) + 1
            })
        } else {
            // Anything else is "Ikke tildelt"
            unassignedCount++
        }
    }

    return { groupCounters, unassignedCount }
}

export function formatWaitingTime(surgery: Surgery) {
    const hasWaitingTime = isUnScheduledSurgery(surgery) && surgery.creationTime
    return hasWaitingTime ? String(diff(getToday(), surgery.creationTime, 'day')) : ''
}

type FormatDateArgs = {
    surgery: Surgery
    asShortFormat?: boolean
}

export function formatTentativeDate({ surgery, asShortFormat = false }: FormatDateArgs) {
    const hasTentativeDate = surgery.contact?.checkInTime && ['TentativeDate', 'TentativeMonth'].includes(surgery.contact.tentative ?? '')
    const monthFormat = asShortFormat ? DATE_TEMPLATES.monthYearShort : DATE_TEMPLATES.monthYear
    return hasTentativeDate
        ? capitalize(format(day(surgery.contact!.checkInTime), surgery.contact!.tentative === 'TentativeMonth' ? monthFormat : DATE_TEMPLATES.norwegianDate))
        : ''
}

export function formatOperationDate({ surgery, asShortFormat = false }: FormatDateArgs) {
    const dateFormat = asShortFormat ? DATE_TEMPLATES.dateShortMonth : DATE_TEMPLATES.norwegianDate

    const hasStartTime = isScheduledSurgery(surgery) && !!surgery.plannedTheatreInTime
    if (hasStartTime) return format(day(surgery.plannedTheatreInTime), dateFormat)

    const hasCheckInTime = surgery.contact?.checkInTime && surgery.contact.tentative === 'NotTentative'
    if (hasCheckInTime) return format(day(surgery.contact!.checkInTime), dateFormat)

    return ''
}

export function getPatientAvailabilityComparable(surgery: Surgery) {
    if (getIsWaiting(surgery)) {
        return '0'
    }
    const availabilities = getPatientAvailabilityFromSurgery(surgery)
    if (availabilities.length === 0) {
        return '3'
    }

    let availableFrom = null
    for (const availability of availabilities) {
        if (availability.type === 'availableFrom') {
            availableFrom = `2-${format(availability.startDate)}`
        }
    }

    // the more unavailability periods there are, the lower the comparable
    const unavailabilityIndicator = Math.max(0, 10 - availabilities.length)

    if (availableFrom !== null) {
        return `${availableFrom}-${unavailabilityIndicator}`
    }

    if (availabilities[0]?.type === 'unavailableBetween') {
        return `1-${format(availabilities[0].startDate)}-${unavailabilityIndicator}`
    }
    return null
}
